import axios from 'axios';

// Get API URL from environment or use default
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.vetmeapp.com/api';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 30000 // 30 seconds
});

// Request interceptor - Add auth token to all requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle common errors
api.interceptors.response.use(
  (response) => {
    // Return the data directly for easier access
    return response;
  },
  (error) => {
    // Handle 401 Unauthorized - token expired or invalid
    if (error.response?.status === 401) {
      // Don't redirect if we're calling /auth/me (initial auth check)
      // or if we're already on the login page
      const isAuthCheck = error.config?.url?.includes('/auth/me');
      const isLoginPage = window.location.pathname === '/login';

      if (!isAuthCheck && !isLoginPage) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        window.location.href = '/login';
      }
    }

    // Handle 403 Forbidden - insufficient permissions
    if (error.response?.status === 403) {
      console.error('Insufficient permissions');
    }

    // Handle network errors
    if (!error.response) {
      console.error('Network error - please check your connection');
    }

    return Promise.reject(error);
  }
);

// Auth endpoints
export const authAPI = {
  login: (email, password) => api.post('/admin/auth/login', { email, password }),
  logout: () => api.post('/admin/auth/logout'),
  getMe: () => api.get('/admin/auth/me'),
  refreshToken: () => api.post('/admin/auth/refresh-token')
};

// Admin user management endpoints
export const adminManagementAPI = {
  getAdmins: (params) => api.get('/admin/admins', { params }),
  getAdmin: (id) => api.get(`/admin/admins/${id}`),
  getAdminStats: () => api.get('/admin/admins/stats'),
  getRoles: () => api.get('/admin/admins/roles'),
  createAdmin: (data) => api.post('/admin/admins', data),
  updateAdmin: (id, data) => api.put(`/admin/admins/${id}`, data),
  changePassword: (id, new_password) => api.put(`/admin/admins/${id}/change-password`, { new_password }),
  deleteAdmin: (id) => api.delete(`/admin/admins/${id}`)
};

// User management endpoints
export const userAPI = {
  getUsers: (params) => api.get('/admin/users', { params }),
  getUser: (id) => api.get(`/admin/users/${id}`),
  searchUsers: (query) => api.get('/admin/users/search', { params: { q: query } }),
  getUserStats: () => api.get('/admin/users/stats/overview'),
  suspendUser: (id, reason) => api.put(`/admin/users/${id}/suspend`, { reason }),
  unsuspendUser: (id) => api.put(`/admin/users/${id}/unsuspend`),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),

  // KYC management
  getUserKYC: (id) => api.get(`/admin/users/${id}/kyc`),
  getPendingKYC: () => api.get('/admin/users/kyc/pending'),
  approveKYC: (id, tier) => api.put(`/admin/users/${id}/kyc/approve`, { tier }),
  rejectKYC: (id, reason) => api.put(`/admin/users/${id}/kyc/reject`, { reason }),

  // User actions
  resetUserPIN: (id) => api.post(`/admin/users/${id}/reset-pin`)
};

// Transaction endpoints
export const transactionAPI = {
  getTransactions: (params) => api.get('/admin/transactions', { params }),
  getTransaction: (id) => api.get(`/admin/transactions/${id}`),
  getTransactionStats: () => api.get('/admin/transactions/stats/overview'),
  approveTransaction: (id) => api.post(`/admin/transactions/${id}/approve`),
  cancelTransaction: (id, reason) => api.post(`/admin/transactions/${id}/cancel`, { reason }),
  refundTransaction: (id, reason) => api.post(`/admin/transactions/${id}/refund`, { reason })
};

// Financial endpoints
export const financialAPI = {
  getCurrentRates: () => api.get('/admin/financial/rates/current'),
  updateRates: (rates) => api.put('/admin/financial/rates', rates),
  createRateOverride: (override) => api.post('/admin/financial/rates/override', override),
  deleteRateOverride: (id) => api.delete(`/admin/financial/rates/override/${id}`),

  getCommissionSettings: () => api.get('/admin/financial/settings/commission'),
  updateCommissionSettings: (settings) => api.put('/admin/financial/settings/commission', settings),
  getCommissionEarnings: (params) => api.get('/admin/financial/commission/earnings', { params }),
  getCommissionStats: (params) => api.get('/admin/financial/commission/stats', { params }),

  getWalletBalances: () => api.get('/admin/financial/wallets/balances')
};

// Quidax endpoints
export const quidaxAPI = {
  getAccounts: (params) => api.get('/admin/quidax/accounts', { params }),
  getFailedAccounts: () => api.get('/admin/quidax/accounts/failed'),
  getAccountStats: () => api.get('/admin/quidax/accounts/stats'),
  retryAccountCreation: (userId) => api.post(`/admin/quidax/accounts/${userId}/retry`),
  syncAccount: (userId) => api.post(`/admin/quidax/accounts/${userId}/sync`)
};

// Referral endpoints
export const referralAPI = {
  getReferrals: (params) => api.get('/admin/referrals', { params }),
  getPendingEarnings: () => api.get('/admin/referrals/pending'),
  getReferralStats: () => api.get('/admin/referrals/stats'),
  getTopReferrers: () => api.get('/admin/referrals/top'),
  processEarningsPayout: (id) => api.post(`/admin/referrals/${id}/payout`)
};

// Smile Identity / BVN endpoints
export const smileAPI = {
  getVerifications: (params) => api.get('/admin/smile/verifications', { params }),
  getPendingVerifications: () => api.get('/admin/smile/verifications/pending'),
  getBVNVerifications: (params) => api.get('/admin/smile/bvn', { params }),
  retryBVNVerification: (jobId) => api.post(`/admin/smile/bvn/${jobId}/retry`),
  getBVNStats: () => api.get('/admin/smile/bvn/stats')
};

// WhatsApp endpoints
export const whatsappAPI = {
  getMessages: (params) => api.get('/admin/whatsapp/messages', { params }),
  broadcastMessage: (data) => api.post('/admin/whatsapp/broadcast', data),
  getTemplates: () => api.get('/admin/whatsapp/templates'),
  createTemplate: (template) => api.post('/admin/whatsapp/templates', template),
  getFlows: () => api.get('/admin/whatsapp/flows'),
  getFlowAnalytics: (id) => api.get(`/admin/whatsapp/flows/${id}/analytics`),
  getFlowSessions: (params) => api.get('/admin/whatsapp/flows/sessions', { params })
};

// System endpoints
export const systemAPI = {
  getHealth: () => api.get('/admin/health'),
  getAuditLogs: (params) => api.get('/admin/system/audit-logs', { params }),
  getErrors: (params) => api.get('/admin/system/errors', { params })
};

// Dashboard summary endpoint (custom aggregated endpoint if available)
export const dashboardAPI = {
  getStats: () => api.get('/admin/dashboard/stats')
};

// Analytics endpoints
export const analyticsAPI = {
  // User analytics
  getUserGrowthStats: () => api.get('/admin/users/stats/growth'),
  getUserActivityStats: () => api.get('/admin/users/stats/activity'),
  getKycStats: () => api.get('/admin/users/kyc/stats'),

  // Transaction analytics
  getVolumeStats: (params) => api.get('/admin/transactions/stats/volume', { params }),
  getRevenueStats: (params) => api.get('/admin/transactions/stats/revenue', { params }),

  // Commission analytics
  getCommissionStats: () => api.get('/admin/financial/commission/stats'),

  // Referral analytics
  getReferralStats: () => api.get('/admin/referrals/stats'),

  // Quidax analytics
  getQuidaxStats: () => api.get('/admin/quidax/accounts/stats'),
};

export default api;
